import React, { useState } from 'react'
import { AlertTriangle, Package, ShoppingCart } from 'lucide-react'
import './ConflictResolutionModal.scss'

const ConflictResolutionModal = ({ conflicts, onResolve, onCancel }) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [resolutions, setResolutions] = useState({})

  if (!conflicts || conflicts.length === 0) return null

  const currentConflict = conflicts[currentIndex]
  const isProduct = currentConflict.type === 'product'
  const totalConflicts = conflicts.length
  const progress = ((currentIndex + 1) / totalConflicts) * 100

  const handleResolve = (decision) => {
    const newResolutions = {
      ...resolutions,
      [currentIndex]: decision,
    }
    setResolutions(newResolutions)

    if (currentIndex < conflicts.length - 1) {
      setCurrentIndex(currentIndex + 1)
    } else {
      // Tous les conflits résolus
      const finalResolutions = conflicts.map((conflict, idx) => ({
        conflict,
        decision: newResolutions[idx],
      }))
      onResolve(finalResolutions)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'GNF',
      minimumFractionDigits: 0,
    }).format(amount)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const renderProductComparison = () => {
    const { existing, imported } = currentConflict

    return (
      <div className='conflict-comparison'>
        <div className='conflict-comparison__grid'>
          {/* Version existante */}
          <div className='conflict-comparison__card conflict-comparison__card--existing'>
            <div className='conflict-comparison__header'>
              <Package size={20} />
              <h4>Version Actuelle</h4>
            </div>
            <div className='conflict-comparison__content'>
              <div className='conflict-comparison__item'>
                <span className='conflict-comparison__label'>Nom:</span>
                <div className='conflict-comparison__value'>
                  {existing.name}
                </div>
              </div>
              <div className='conflict-comparison__item'>
                <span className='conflict-comparison__label'>Code:</span>
                <div className='conflict-comparison__value'>
                  {existing.code}
                </div>
              </div>
              <div className='conflict-comparison__item'>
                <span className='conflict-comparison__label'>Prix:</span>
                <div className='conflict-comparison__value'>
                  {formatCurrency(existing.basePrice)}
                </div>
              </div>
              <div className='conflict-comparison__item'>
                <span className='conflict-comparison__label'>
                  Dernière modification:
                </span>
                <div className='conflict-comparison__value'>
                  {existing.updatedAt
                    ? formatDate(existing.updatedAt)
                    : 'Non disponible'}
                </div>
              </div>
              {existing.description && (
                <div className='conflict-comparison__item'>
                  <span className='conflict-comparison__label'>
                    Description:
                  </span>
                  <div className='conflict-comparison__value conflict-comparison__value--small'>
                    {existing.description}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Version importée */}
          <div className='conflict-comparison__card conflict-comparison__card--imported'>
            <div className='conflict-comparison__header'>
              <Package size={20} />
              <h4>Version Importée</h4>
            </div>
            <div className='conflict-comparison__content'>
              <div className='conflict-comparison__item'>
                <span className='conflict-comparison__label'>Nom:</span>
                <div className='conflict-comparison__value'>
                  {imported.name}
                </div>
              </div>
              <div className='conflict-comparison__item'>
                <span className='conflict-comparison__label'>Code:</span>
                <div className='conflict-comparison__value'>
                  {imported.code}
                </div>
              </div>
              <div className='conflict-comparison__item'>
                <span className='conflict-comparison__label'>Prix:</span>
                <div className='conflict-comparison__value'>
                  {formatCurrency(imported.basePrice)}
                </div>
              </div>
              <div className='conflict-comparison__item'>
                <span className='conflict-comparison__label'>
                  Dernière modification:
                </span>
                <div className='conflict-comparison__value'>
                  {imported.updatedAt
                    ? formatDate(imported.updatedAt)
                    : 'Non disponible'}
                </div>
              </div>
              {imported.description && (
                <div className='conflict-comparison__item'>
                  <span className='conflict-comparison__label'>
                    Description:
                  </span>
                  <div className='conflict-comparison__value conflict-comparison__value--small'>
                    {imported.description}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Détection des différences */}
        <div className='conflict-differences'>
          <h5>Différences détectées:</h5>
          <ul>
            {existing.name !== imported.name && <li>• Nom différent</li>}
            {existing.basePrice !== imported.basePrice && (
              <li>• Prix différent</li>
            )}
            {existing.description !== imported.description && (
              <li>• Description différente</li>
            )}
            {currentConflict.reason === 'code_conflict' && (
              <li>• Même code mais produit totalement différent</li>
            )}
            {!(existing.name !== imported.name) &&
              !(existing.basePrice !== imported.basePrice) &&
              !(existing.description !== imported.description) &&
              !(currentConflict.reason === 'code_conflict') && (
                <li>• Même produit déjà existant</li>
              )}
          </ul>
        </div>
      </div>
    )
  }

  const renderPurchaseComparison = () => {
    const { existing, imported } = currentConflict

    return (
      <div className='conflict-comparison'>
        <div className='conflict-comparison__grid'>
          {/* Version existante */}
          <div className='conflict-comparison__card conflict-comparison__card--existing'>
            <div className='conflict-comparison__header'>
              <ShoppingCart size={20} />
              <h4>Version Actuelle</h4>
            </div>
            <div className='conflict-comparison__content'>
              <div className='conflict-comparison__item'>
                <span className='conflict-comparison__label'>Client:</span>
                <div className='conflict-comparison__value'>
                  {existing.clientName}
                </div>
              </div>
              <div className='conflict-comparison__item'>
                <span className='conflict-comparison__label'>Téléphone:</span>
                <div className='conflict-comparison__value'>
                  {existing.clientPhone || 'Non renseigné'}
                </div>
              </div>
              <div className='conflict-comparison__item'>
                <span className='conflict-comparison__label'>Date:</span>
                <div className='conflict-comparison__value'>
                  {formatDate(existing.date)}
                </div>
              </div>
              <div className='conflict-comparison__item'>
                <span className='conflict-comparison__label'>
                  Montant total:
                </span>
                <div className='conflict-comparison__value conflict-comparison__value--bold'>
                  {formatCurrency(existing.totalAmount)}
                </div>
              </div>
              <div className='conflict-comparison__item'>
                <span className='conflict-comparison__label'>
                  Produits ({existing.items?.length || 0}):
                </span>
                <div className='conflict-comparison__products'>
                  {existing.items?.slice(0, 3).map((item, idx) => (
                    <div key={idx}>
                      • {item.name} x{item.quantity}
                    </div>
                  ))}
                  {existing.items?.length > 3 && (
                    <div>• + {existing.items.length - 3} autre(s)...</div>
                  )}
                </div>
              </div>
              <div className='conflict-comparison__item'>
                <span className='conflict-comparison__label'>
                  Dernière modification:
                </span>
                <div className='conflict-comparison__value conflict-comparison__value--small'>
                  {existing.updatedAt
                    ? formatDate(existing.updatedAt)
                    : 'Non disponible'}
                </div>
              </div>
            </div>
          </div>

          {/* Version importée */}
          <div className='conflict-comparison__card conflict-comparison__card--imported'>
            <div className='conflict-comparison__header'>
              <ShoppingCart size={20} />
              <h4>Version Importée</h4>
            </div>
            <div className='conflict-comparison__content'>
              <div className='conflict-comparison__item'>
                <span className='conflict-comparison__label'>Client:</span>
                <div className='conflict-comparison__value'>
                  {imported.clientName}
                </div>
              </div>
              <div className='conflict-comparison__item'>
                <span className='conflict-comparison__label'>Téléphone:</span>
                <div className='conflict-comparison__value'>
                  {imported.clientPhone || 'Non renseigné'}
                </div>
              </div>
              <div className='conflict-comparison__item'>
                <span className='conflict-comparison__label'>Date:</span>
                <div className='conflict-comparison__value'>
                  {formatDate(imported.date)}
                </div>
              </div>
              <div className='conflict-comparison__item'>
                <span className='conflict-comparison__label'>
                  Montant total:
                </span>
                <div className='conflict-comparison__value conflict-comparison__value--bold'>
                  {formatCurrency(imported.totalAmount)}
                </div>
              </div>
              <div className='conflict-comparison__item'>
                <span className='conflict-comparison__label'>
                  Produits ({imported.items?.length || 0}):
                </span>
                <div className='conflict-comparison__products'>
                  {imported.items?.slice(0, 3).map((item, idx) => (
                    <div key={idx}>
                      • {item.name} x{item.quantity}
                    </div>
                  ))}
                  {imported.items?.length > 3 && (
                    <div>• + {imported.items.length - 3} autre(s)...</div>
                  )}
                </div>
              </div>
              <div className='conflict-comparison__item'>
                <span className='conflict-comparison__label'>
                  Dernière modification:
                </span>
                <div className='conflict-comparison__value conflict-comparison__value--small'>
                  {imported.updatedAt
                    ? formatDate(imported.updatedAt)
                    : 'Non disponible'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Détection des différences */}
        <div className='conflict-differences'>
          <h5>Différences détectées:</h5>
          <ul>
            {existing.totalAmount !== imported.totalAmount && (
              <li>• Montant total différent</li>
            )}
            {existing.items?.length !== imported.items?.length && (
              <li>
                • Nombre de produits différent ({existing.items?.length} vs{' '}
                {imported.items?.length})
              </li>
            )}
            {existing.clientPhone !== imported.clientPhone && (
              <li>• Téléphone différent</li>
            )}
            {!(existing.totalAmount !== imported.totalAmount) &&
              !(existing.items?.length !== imported.items?.length) &&
              !(existing.clientPhone !== imported.clientPhone) && (
                <li>• Autres différences (ex: nom, statut de livraison ...)</li>
              )}
          </ul>
        </div>
      </div>
    )
  }

  return (
    <div className='conflict-modal'>
      <div className='conflict-modal__container'>
        <div className='conflict-modal__content'>
          {/* En-tête */}
          <div className='conflict-modal__header'>
            <AlertTriangle size={32} className='conflict-modal__icon' />
            <div className='conflict-modal__header-text'>
              <h2>Conflit détecté</h2>
              <p>
                Conflit {currentIndex + 1} sur {totalConflicts}
              </p>
            </div>
          </div>

          {/* Barre de progression */}
          <div className='conflict-modal__progress'>
            <div className='conflict-modal__progress-bar'>
              <div
                className='conflict-modal__progress-fill'
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Message d'explication */}
          <div className='conflict-modal__info'>
            <p>
              {isProduct
                ? 'Un produit dans le fichier importé entre en conflit avec un produit existant. Choisissez comment gérer ce conflit.'
                : 'Un achat dans le fichier importé entre en conflit avec un achat existant. Choisissez comment gérer ce conflit.'}
            </p>
          </div>

          {/* Comparaison */}
          {isProduct ? renderProductComparison() : renderPurchaseComparison()}

          {/* Boutons de décision */}
          <div className='conflict-modal__actions'>
            <button
              onClick={() => handleResolve('keep')}
              className='conflict-modal__action-btn conflict-modal__action-btn--keep'
            >
              <div className='conflict-modal__action-title'>Ignorer</div>
              <div className='conflict-modal__action-desc'>
                Garder la version actuelle et ignorer l'importation
              </div>
            </button>

            <button
              onClick={() => handleResolve('replace')}
              className='conflict-modal__action-btn conflict-modal__action-btn--replace'
            >
              <div className='conflict-modal__action-title'>Remplacer</div>
              <div className='conflict-modal__action-desc'>
                Remplacer la version actuelle par celle importée
              </div>
            </button>

            <button
              onClick={() => handleResolve('add_new')}
              className='conflict-modal__action-btn conflict-modal__action-btn--add'
            >
              <div className='conflict-modal__action-title'>
                Ajouter nouveau
              </div>
              <div className='conflict-modal__action-desc'>
                {isProduct
                  ? 'Ajouter comme nouveau produit (avec code modifié)'
                  : 'Ajouter comme nouvel achat distinct'}
              </div>
            </button>
          </div>

          {/* Bouton annuler */}
          <div className='conflict-modal__footer'>
            <button onClick={onCancel} className='conflict-modal__cancel-btn'>
              Annuler l'importation
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ConflictResolutionModal
